import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBlockedUsers, unblockUser, type BlockedUserDto } from '../api/block';
import { ApiError } from '../api/client';
import { HomeFooter } from '../components/home/HomeFooter';
import { HomeHeader } from '../components/home/HomeHeader';
import { useGuestGate } from '../components/home/GuestGateProvider';
import { ChevronIcon } from '../components/shared/Icons';
import { useToast } from '../components/ui/ToastProvider';
import { resolveProfileImageUrl } from '../config/media';
import { HEADER_SEARCH_KEYWORD_SUGGESTIONS } from '../config/searchSuggestions';
import { loadAuthSession } from '../data/authSession';
import { removeBlockedUserId } from '../data/blockedUsers';
import '../styles/blocked-users.css';

const PAGE_SIZE = 20;

function formatDateLabel(value: string | null | undefined) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  const year = String(parsed.getFullYear()).slice(-2);
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

export function BlockedUsersPage() {
  const navigate = useNavigate();
  const { openGuestGate } = useGuestGate();
  const { showToast } = useToast();
  const isAuthenticated = Boolean(loadAuthSession());

  const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const headerSearchRef = useRef<HTMLDivElement | null>(null);

  const [items, setItems] = useState<BlockedUserDto[]>([]);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [unblockingId, setUnblockingId] = useState<string | null>(null);

  const filteredSuggestions = HEADER_SEARCH_KEYWORD_SUGGESTIONS.filter((item) =>
    item.toLowerCase().includes(headerSearchQuery.toLowerCase()),
  );

  const handleSearchSubmit = useCallback(
    (value: string) => {
      const q = value.trim();
      if (!q) return;
      navigate(`/search?q=${encodeURIComponent(q)}`);
    },
    [navigate],
  );

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!headerSearchRef.current?.contains(event.target as Node)) {
        setHeaderSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      openGuestGate('/account/blocked-users');
      return;
    }

    let active = true;
    setLoading(true);
    setErrorMessage('');

    getBlockedUsers({ page: 0, size: PAGE_SIZE })
      .then((result) => {
        if (!active) return;
        setItems(result.items);
        setPage(result.page);
        setHasNext(result.hasNext);
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof ApiError && error.status === 401) {
          openGuestGate('/account/blocked-users');
          return;
        }
        setErrorMessage(getErrorMessage(error, '차단 목록을 불러오지 못했습니다.'));
        setItems([]);
        setHasNext(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isAuthenticated, openGuestGate]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasNext) {
      return;
    }
    setLoadingMore(true);
    setErrorMessage('');
    try {
      const nextPage = page + 1;
      const result = await getBlockedUsers({ page: nextPage, size: PAGE_SIZE });
      setItems((prev) => {
        const seen = new Set(prev.map((row) => row.userId));
        return [...prev, ...result.items.filter((row) => !seen.has(row.userId))];
      });
      setPage(result.page);
      setHasNext(result.hasNext);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, '차단 목록을 더 불러오지 못했습니다.'));
    } finally {
      setLoadingMore(false);
    }
  }, [hasNext, loadingMore, page]);

  const handleUnblock = useCallback(
    async (user: BlockedUserDto) => {
      if (unblockingId) {
        return;
      }
      setUnblockingId(user.userId);
      setErrorMessage('');
      try {
        await unblockUser(user.userId);
        removeBlockedUserId(user.userId);
        setItems((prev) => prev.filter((row) => row.userId !== user.userId));
        showToast({ message: `${user.nickname}님을 차단 해제했습니다.`, type: 'success' });
      } catch (error) {
        setErrorMessage(getErrorMessage(error, '차단 해제에 실패했습니다.'));
      } finally {
        setUnblockingId(null);
      }
    },
    [showToast, unblockingId],
  );

  return (
    <main className="blocked-users-page">
      <HomeHeader
        authenticated={isAuthenticated}
        filteredSuggestions={filteredSuggestions}
        onGuestCta={() => navigate('/login')}
        onSearchChange={(value) => {
          setHeaderSearchQuery(value);
          setHeaderSearchOpen(Boolean(value.trim()));
        }}
        onSearchClear={() => {
          setHeaderSearchQuery('');
          setHeaderSearchOpen(false);
        }}
        onSearchFocus={() => setHeaderSearchOpen(Boolean(headerSearchQuery.trim()))}
        onSearchSubmit={handleSearchSubmit}
        onSuggestionSelect={(value) => {
          setHeaderSearchOpen(false);
          handleSearchSubmit(value);
        }}
        searchOpen={headerSearchOpen}
        searchQuery={headerSearchQuery}
        searchRef={headerSearchRef}
      />

      <div className="blocked-users-page__main">
        <div className="blocked-users">
          <header className="blocked-users__header">
            <button
              aria-label="뒤로"
              className="blocked-users__back"
              onClick={() => navigate(-1)}
              type="button"
            >
              <span aria-hidden className="blocked-users__back-chevron">
                <ChevronIcon />
              </span>
            </button>
            <h1 className="blocked-users__title">차단한 사용자</h1>
          </header>

          {loading ? (
            <p className="blocked-users__feedback" role="status">
              차단 목록을 불러오는 중입니다.
            </p>
          ) : errorMessage && items.length === 0 ? (
            <p className="blocked-users__feedback blocked-users__feedback--error" role="alert">
              {errorMessage}
            </p>
          ) : items.length === 0 ? (
            <p className="blocked-users__feedback blocked-users__feedback--empty">
              차단한 사용자가 없습니다.
            </p>
          ) : (
            <>
              <ul className="blocked-users__list">
                {items.map((user) => {
                  const avatar = resolveProfileImageUrl(null, user.profileImageUrl);
                  return (
                    <li className="blocked-users__row" key={user.userId}>
                      <div className="blocked-users__user">
                        {avatar ? (
                          <img
                            alt=""
                            className="blocked-users__avatar"
                            height={44}
                            src={avatar}
                            width={44}
                          />
                        ) : (
                          <div className="blocked-users__avatar blocked-users__avatar--empty" />
                        )}
                        <div className="blocked-users__user-text">
                          <span className="blocked-users__nickname">{user.nickname}</span>
                          <span className="blocked-users__blocked-at">
                            {formatDateLabel(user.blockedAt)} 차단
                          </span>
                        </div>
                      </div>
                      <button
                        className="blocked-users__unblock"
                        disabled={unblockingId === user.userId}
                        onClick={() => void handleUnblock(user)}
                        type="button"
                      >
                        {unblockingId === user.userId ? '해제 중...' : '차단 해제'}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {errorMessage ? (
                <p
                  className="blocked-users__feedback blocked-users__feedback--error"
                  role="alert"
                >
                  {errorMessage}
                </p>
              ) : null}

              {hasNext ? (
                <button
                  className="blocked-users__more"
                  disabled={loadingMore}
                  onClick={() => void loadMore()}
                  type="button"
                >
                  {loadingMore ? '불러오는 중...' : '더 보기'}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>

      <HomeFooter />
    </main>
  );
}
